import { useState } from 'react';
import type { CarryForwardPayload, TrialBalanceRow } from '@/core/models/trial-balance.models';
import { notifyError } from '@/core/notify/notify';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';

export interface ImportTrailBalanceDialogData {
  /** The client's other trial balances; each shows its label field. */
  options: TrialBalanceRow[];
}

/** Angular ImportTrailBalanceComponent: "Carry Forward Trial Balance". Closes with the CarryForwardPayload. */
export function ImportTrailBalance({
  data,
  dialogRef,
}: DialogComponentProps<ImportTrailBalanceDialogData, CarryForwardPayload>) {
  const options = data?.options ?? [];
  const [sourceTrialBalanceId, setSourceTrialBalanceId] = useState<string | null>(null);
  const [broughtForwardOnly, setBroughtForwardOnly] = useState(false);
  /** The dialog is closing with the choice: a second click cannot send it twice. */
  const [closing, setClosing] = useState(false);

  const onImport = () => {
    if (!sourceTrialBalanceId) {
      notifyError(
        options.length === 0
          ? 'This client has no other trial balance to import balances from.'
          : 'Select the trial balance to import balances from.',
      );
      return;
    }
    setClosing(true);
    dialogRef.close({ sourceTrialBalanceId, broughtForwardOnly: !!broughtForwardOnly });
  };

  return (
    <div className="mat-dialog-wrapper new-account">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Carry Forward Trial Balance</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="form-field">
              <p>Trial Balance</p>
              <Select<TrialBalanceRow>
                className="w-387"
                name="sourceTrialBalanceId"
                items={options}
                bindLabel="label"
                bindValue="id"
                value={sourceTrialBalanceId}
                onChange={(value) => setSourceTrialBalanceId((value as string | null) ?? null)}
              />
            </div>

            <div>
              <Checkbox
                name="broughtForwardOnly"
                checked={broughtForwardOnly}
                onChange={(event) => setBroughtForwardOnly(event.target.checked)}
              >
                Brought Forward Only
              </Checkbox>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" type="button" busy={closing} onClick={onImport}>
              Import Balance
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
